import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation } from "react-query";
import _ from "lodash";
import {
  Box,
  FormControl,
  FormHelperText,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { Asterisk } from "../../common/components/Asterisk";
import { SectionTitle } from "../../common/components/SectionTitle";
import { ButtonSubmitA } from "../../common/components/ButtonSubmitA";
import { ButtonCancelA } from "../../common/components/ButtonCancelA";
import { fetchUpdateUser } from "../miscellaneous/fetchUpdateUser";
import { fetchGetUserDetail } from "../miscellaneous/fetchGetUserDetail";
import { fetchGetCompaniesByParent } from "../../organization-management/miscellaneous/fetchGetCompaniesByParent";
import { fetchGetAllParentCompanies } from "../miscellaneous/fetchGetAllParentCompanies";
import { fetchGetJobsByCompanyId } from "../../jobs-management/miscellaneous/fetchGetJobsByCompanyId";
import { AxiosError } from "axios";

const PARTICIPANT_TYPES = [
  { value: 1, label: "Time Study Participant" },
  { value: 2, label: "Ops Work Log Participant" },
];

const formControlStyle = {
  marginTop: "20px",
};

const btnGroupStyle = {
  display: "flex",
  flexDirection: "row",
  justifyContent: "center",
  gap: "20px",
  marginTop: "60px",
};

export const UpdateUserDetail: FC = () => {
  const navigate = useNavigate();
  const { userId } = useParams();
  const [user, setUser] = useState<any>(null);

  const userRole = !!localStorage.getItem("rolesJson")
    ? JSON.parse(localStorage.getItem("rolesJson") as string)
    : [];

  const isRoleSupperAdmin =
    userRole &&
    userRole.length > 0 &&
    userRole[0]?.Name &&
    userRole[0]?.Name.toLowerCase() === "super admin";

  const callerParentCompanyId = localStorage.getItem("parentCompanyId") ?? "";
  const callerParentCompanyName = localStorage.getItem("parentCompanyName") ?? "";

  const [parentCompanies, setParentCompanies] = useState<{ id: string; name: string }[]>(
    !isRoleSupperAdmin && !!callerParentCompanyId
      ? [{ id: callerParentCompanyId, name: callerParentCompanyName }]
      : []
  );

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    reset,
    setError,
    clearErrors,
    watch,
  } = useForm();

  const selectedRole = watch("role");
  const showCompany = selectedRole !== "Super Admin";
  const showOrg = selectedRole === "User";
  const showJobTitle = selectedRole === "User";
  const showParticipantType = selectedRole === "User";

  const mutationGetUser = useMutation(fetchGetUserDetail, {
    onSuccess: (res) => {
      if (res.status === 200) {
        setUser(res.data);
        const userDataRole = res.data.role;

        // Only fetch companies/jobs if the user's role requires them
        if (userDataRole === "User" && res.data.companyId) {
          mutationGetJobsByCompany.mutate(res.data.companyId);
        }
        if (isRoleSupperAdmin && res.data.parentCompanyId && userDataRole !== "Super Admin") {
          mutationGetCompaniesByParent.mutate(res.data.parentCompanyId);
          setParentCompanies((prev) => {
            const alreadySet = prev.some((p) => p.id === res.data.parentCompanyId);
            if (!alreadySet) {
              return [...prev, { id: res.data.parentCompanyId, name: res.data.parentCompanyName ?? "" }];
            }
            return prev;
          });
        }
      }
    },
    onError: () => {},
  });

  const mutationUpdateUser = useMutation(fetchUpdateUser, {
    onSuccess: (response) => {
      if (response && response.status === 200) {
        navigate("/user-management");
      }
    },
    onError: (error: AxiosError) => {
      if (
        error.response &&
        error.response.status === 400 &&
        (error.response.data as string).includes("already taken")
      ) {
        reset(
          {},
          {
            keepValues: true,
          }
        );

        setError("email", {
          type: "manual",
          message: "The e-mail address is already in use",
        });
      }
    },
  });

  const [companiesName, setCompaniesName] = useState([]);
  const [jobTitle, setJobTitle] = useState([]);

  const mutationGetParentCompanies = useMutation(fetchGetAllParentCompanies, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companies = _.map(res.data.data, (item: any) => ({
          id: item.id,
          name: item.companyName,
        }));
        setParentCompanies(companies as any);
      }
    },
    onError: () => {},
  });

  const mutationGetCompaniesByParent = useMutation(fetchGetCompaniesByParent, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const companyName = _.map(res.data.data, (item: any) => ({
          name: item.companyName,
          value: item.id,
        }));
        setCompaniesName(companyName as any);
      }
    },
    onError: () => {},
  });

  const mutationGetJobsByCompany = useMutation(fetchGetJobsByCompanyId, {
    onSuccess: (res) => {
      if (res.status === 200) {
        const jobs = _.map(res.data.data ?? [], (item) => {
          return { id: item.id, name: item.title };
        });

        setJobTitle(jobs as any);
      }
    },
    onError: () => {},
  });

  useEffect(() => {
    mutationGetUser.mutate(userId as string);
    if (isRoleSupperAdmin) {
      mutationGetParentCompanies.mutate();
    } else if (callerParentCompanyId) {
      mutationGetCompaniesByParent.mutate(callerParentCompanyId);
    }
  }, []);

  const onSubmit = (data: any) => {
    const role = data.role;

    const companyName = role === "User"
      ? _.find(companiesName ?? [], (item: any) => item.value === data.organization)
      : null;

    const userData = {
      id: userId,
      companyName: companyName?.name ?? null,
      fullName: data.fullName,
      email: data.email,
      role: data.role,
      isActive: getValues("isActive") === "true" ? true : false,
      jobTitleId: role === "User" ? data.jobTitle : null,
      participantType: data.participantType || 1,
      parentCompanyId: role === "Admin" ? data.parentCompany : null,
    };

    mutationUpdateUser.mutate(userData);

    if (mutationUpdateUser.isSuccess) {
      navigate("/user-management");
    }
  };

  const handleCancel = () => {
    navigate("/user-management");
  };

  return !!user ? (
    <div className="mt-9">
      <SectionTitle name="Update User Information" />
      <Box sx={{ minWidth: 120, marginTop: "60px" }}>
        <form className="pr-24 pl-24 pb-24" onSubmit={handleSubmit(onSubmit)}>
          {showCompany && (
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Company <Asterisk />
            </label>
            <Controller
              control={control}
              name="parentCompany"
              defaultValue={user.parentCompanyId ?? ""}
              rules={{ required: "Company is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="parentCompany"
                  value={value ?? ""}
                  disabled={!isRoleSupperAdmin}
                  onChange={(e) => {
                    setValue("parentCompany", e.target.value);
                    clearErrors("parentCompany");
                    setValue("organization", "");
                    setValue("jobTitle", "");
                    setJobTitle([]);
                    setCompaniesName([]);
                    if (e.target.value) {
                      mutationGetCompaniesByParent.mutate(e.target.value);
                    }
                  }}
                  style={{ height: "38px" }}
                >
                  {parentCompanies.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("parentCompany").error?.message}
            </FormHelperText>
          </FormControl>
          )}

          {showOrg && (
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Organization <Asterisk />
            </label>
            <Controller
              control={control}
              name="organization"
              defaultValue={user.companyId}
              rules={{ required: "Organization is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="organization"
                  value={value}
                  onChange={(e) => {
                    setValue("organization", e.target.value);
                    mutationGetJobsByCompany.mutate(e.target.value);
                  }}
                  style={{ height: "38px" }}
                >
                  {(companiesName ?? []).map((item: any) => {
                    return (
                      <MenuItem key={item.value} value={item.value}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("organization").error?.message}
            </FormHelperText>
          </FormControl>
          )}

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              User Email <Asterisk />
            </label>
            <TextField
              {...register("email", {
                required: "User Email is required",
                pattern:
                  /^(?=.{1,254}$)(?=.{1,64}@)[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/,
              })}
              defaultValue={user.email}
              sx={{
                "& .MuiInputBase-input": {
                  height: "5px",
                },
              }}
            />
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("email").error?.message}
              {errors.email &&
                !control.getFieldState("email").error?.message && (
                  <span>Please enter valid email address</span>
                )}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Full Name <Asterisk />
            </label>
            <TextField
              {...register("fullName", { required: "Full Name is required" })}
              defaultValue={user.fullName}
              sx={{
                "& .MuiInputBase-input": {
                  height: "5px",
                },
              }}
            />
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("fullName").error?.message}
            </FormHelperText>
          </FormControl>

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Role <Asterisk />
            </label>
            <Controller
              control={control}
              name="role"
              rules={{ required: "Role is required" }}
              defaultValue={user.role}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="user-role"
                  value={value ?? ""}
                  onChange={(e) => {
                    const newRole = e.target.value;
                    setValue("role", newRole);
                    clearErrors("role");

                    // Clear fields that will be hidden based on new role
                    if (newRole === "Super Admin") {
                      setValue("parentCompany", "");
                      setValue("organization", "");
                      setValue("jobTitle", "");
                      setValue("participantType", "");
                      clearErrors("parentCompany");
                      clearErrors("organization");
                      clearErrors("jobTitle");
                      clearErrors("participantType");
                      setCompaniesName([]);
                      setJobTitle([]);
                    } else if (newRole === "Admin") {
                      setValue("organization", "");
                      setValue("jobTitle", "");
                      setValue("participantType", "");
                      clearErrors("organization");
                      clearErrors("jobTitle");
                      clearErrors("participantType");
                      setJobTitle([]);
                    } else if (newRole === "User") {
                      // Switching to User role — need to reload companies if parent is selected
                      const parentCompanyVal = getValues("parentCompany");
                      if (parentCompanyVal) {
                        mutationGetCompaniesByParent.mutate(parentCompanyVal);
                      }
                    }
                  }}
                  style={{ height: "38px" }}
                >
                  {isRoleSupperAdmin && (
                    <MenuItem value={"Super Admin"}>Super Admin</MenuItem>
                  )}
                  <MenuItem value={"Admin"}>Admin</MenuItem>
                  <MenuItem value={"User"}>User</MenuItem>
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("role").error?.message}
            </FormHelperText>
          </FormControl>

          {showJobTitle && (
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Job Title <Asterisk />
            </label>
            <Controller
              control={control}
              name="jobTitle"
              defaultValue={user.jobTitleId}
              rules={{ required: "Job Title is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="user-job-title"
                  value={value ?? ""}
                  onChange={(e) => {
                    setValue("jobTitle", e.target.value);
                    clearErrors("jobTitle");
                  }}
                  style={{ height: "38px" }}
                >
                  {(jobTitle ?? []).map((item: any) => {
                    return (
                      <MenuItem key={item.id} value={item.id}>
                        {item.name}
                      </MenuItem>
                    );
                  })}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("jobTitle").error?.message}
            </FormHelperText>
          </FormControl>
          )}

          {showParticipantType && (
          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">
              Participant Type <Asterisk />
            </label>
            <Controller
              control={control}
              name="participantType"
              defaultValue={user.participantType ?? 1}
              rules={{ required: "Participant Type is required" }}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="participantType"
                  value={value ?? 1}
                  onChange={(e) => {
                    setValue("participantType", e.target.value);
                    clearErrors("participantType");
                  }}
                  style={{ height: "38px" }}
                >
                  {PARTICIPANT_TYPES.map((item) => (
                    <MenuItem key={item.value} value={item.value}>
                      {item.label}
                    </MenuItem>
                  ))}
                </Select>
              )}
            ></Controller>
            <FormHelperText sx={{ color: "red" }}>
              {control.getFieldState("participantType").error?.message}
            </FormHelperText>
          </FormControl>
          )}

          <FormControl fullWidth sx={formControlStyle}>
            <label className="font-bold">Status</label>
            <Controller
              control={control}
              name="isActive"
              defaultValue={user.isActive ? "true" : "false"}
              render={({ field: { onChange, onBlur, value } }) => (
                <Select
                  id="user-status"
                  value={value ?? "true"}
                  onChange={(e) => {
                    setValue("isActive", e.target.value);
                  }}
                  style={{ height: "38px" }}
                >
                  <MenuItem value={"true"}>Active</MenuItem>
                  <MenuItem value={"false"}>Inactive</MenuItem>
                </Select>
              )}
            ></Controller>
          </FormControl>

          <FormControl fullWidth sx={btnGroupStyle}>
            <ButtonCancelA name="Cancel" onHandleClick={handleCancel} />
            <ButtonSubmitA name="Save" />
          </FormControl>
        </form>
      </Box>
    </div>
  ) : null;
};
